import { Notify } from "@/components/ui/notify";
import { useLineBatches } from "./hooks";

interface UpstreamLineBatchesProps {
  lineId: string;
  lineName: string;
  selected: string[];
  onToggle: (runId: string) => void;
  formatWhen: (iso: string) => string;
}

/**
 * Spec 084 — as bateladas de UMA linha a montante, dentro do seletor de origem.
 *
 * Componente próprio porque cada linha a montante precisa da sua consulta, e consulta em laço é
 * hook em laço. A lista é a mesma paginada do cartão de bateladas, recortada nas últimas: quem
 * associa origem está olhando o material que acabou de chegar, não o arquivo.
 */
export function UpstreamLineBatches({
  lineId,
  lineName,
  selected,
  onToggle,
  formatWhen,
}: UpstreamLineBatchesProps) {
  const query = useLineBatches(lineId, 10);

  return (
    <fieldset className="space-y-1.5">
      <legend className="text-[13px] font-medium text-text-primary">{lineName}</legend>
      {query.isError ? (
        <Notify.QueryError
          error={query.error}
          onRetry={() => void query.refetch()}
          variant="inline"
        />
      ) : query.isLoading ? (
        <p className="text-[12px] text-text-secondary">Carregando bateladas…</p>
      ) : (query.data?.items.length ?? 0) === 0 ? (
        <p className="text-[12px] text-text-tertiary">Nenhuma batelada nesta etapa ainda.</p>
      ) : (
        <ul className="space-y-1">
          {query.data?.items.map((b) => (
            <li key={b.runId} className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                id={`origin-${b.runId}`}
                className="accent-rd-primary-500 h-3.5 w-3.5"
                checked={selected.includes(b.runId)}
                onChange={() => onToggle(b.runId)}
              />
              <label htmlFor={`origin-${b.runId}`} className="min-w-0 flex-1 truncate">
                <span className="font-mono text-text-primary">{b.batchCode}</span>
                <span className="text-text-tertiary"> · {formatWhen(b.startedAt)}</span>
                {b.characteristics.length > 0 ? (
                  <span className="text-text-secondary">
                    {" · "}
                    {b.characteristics
                      .map((c) => `${c.name}: ${c.labels.join(", ") || "não informado"}`)
                      .join(" · ")}
                  </span>
                ) : null}
              </label>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}
